import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, LogOut, Mail, Phone, Calendar, MessageSquare, Eye, EyeOff, FileText, Sparkles, Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp, Users, History, BarChart3, UserCheck, UserX, Key, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [aiApps, setAiApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contacts');
  const [userSubTab, setUserSubTab] = useState('users');
  
  // Article form state
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '', description: '', content: '', category: 'Artificial Intelligence',
    author: 'PSN Team', read_time: '5 min read', category_color: 'bg-blue-100 text-blue-800',
    border_color: 'border-t-blue-500'
  });

  // AI App form state
  const [showAppForm, setShowAppForm] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [appForm, setAppForm] = useState({
    name: '', description: '', category: 'kids', url: '', icon: '🤖',
    age_range: '', duration: '', is_coming_soon: false,
    bg_color: 'from-blue-50 to-indigo-50', border_color: 'border-blue-300'
  });

  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '', email: '', phone: '', role: 'Client', password: ''
  });

  // Reset password modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  const categoryOptions = [
    { value: 'Artificial Intelligence', color: 'bg-green-100 text-green-800', border: 'border-t-green-500' },
    { value: 'Accounting', color: 'bg-orange-100 text-orange-800', border: 'border-t-orange-500' },
    { value: 'Estate Planning', color: 'bg-blue-100 text-blue-800', border: 'border-t-blue-500' },
    { value: 'Bookkeeping & Accounting Automation', color: 'bg-purple-100 text-purple-800', border: 'border-t-purple-500' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchAllData(token);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const token = response.data.token;
      localStorage.setItem('admin_token', token);
      setIsAuthenticated(true);
      fetchAllData(token);
    } catch (err) {
      setError('Invalid credentials. Please try again.');
      setPassword('');
    }
  };

  const fetchAllData = async (token) => {
    setLoading(true);
    setError('');
    try {
      const [contactsRes, articlesRes, appsRes, usersRes, historyRes, visitorsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/admin/articles`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/admin/ai-apps`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/login-history`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/visitors`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      setSubmissions(contactsRes.data || []);
      setArticles(articlesRes.data || []);
      setAiApps(appsRes.data || []);
      setUsers(usersRes.data || []);
      setLoginHistory(historyRes.data || []);
      setVisitorStats(visitorsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError(`Failed to load data: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setSubmissions([]);
    setArticles([]);
    setAiApps([]);
    setUsers([]);
    setLoginHistory([]);
    setVisitorStats(null);
    setPassword('');
  };

  const getToken = () => localStorage.getItem('admin_token');

  // Article CRUD functions
  const handleSaveArticle = async () => {
    const token = getToken();
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, articleForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/articles`, articleForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      fetchAllData(token);
      resetArticleForm();
    } catch (err) {
      setError('Failed to save article');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title, description: article.description, content: article.content,
      category: article.category, author: article.author, read_time: article.read_time,
      category_color: article.category_color, border_color: article.border_color
    });
    setShowArticleForm(true);
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/admin/articles/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData(token);
    } catch (err) {
      setError('Failed to delete article');
    }
  };

  const resetArticleForm = () => {
    setShowArticleForm(false);
    setEditingArticle(null);
    setArticleForm({
      title: '', description: '', content: '', category: 'Artificial Intelligence',
      author: 'PSN Team', read_time: '5 min read', category_color: 'bg-blue-100 text-blue-800',
      border_color: 'border-t-blue-500'
    });
  };

  // AI App CRUD functions
  const handleSaveApp = async () => {
    const token = getToken();
    try {
      if (editingApp) {
        await axios.put(`${API}/admin/ai-apps/${editingApp.id}`, appForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/ai-apps`, appForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      fetchAllData(token);
      resetAppForm();
    } catch (err) {
      setError('Failed to save AI app');
    }
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setAppForm({
      name: app.name, description: app.description, category: app.category,
      url: app.url || '', icon: app.icon, age_range: app.age_range || '',
      duration: app.duration || '', is_coming_soon: app.is_coming_soon,
      bg_color: app.bg_color, border_color: app.border_color
    });
    setShowAppForm(true);
  };

  const handleDeleteApp = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AI app?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/admin/ai-apps/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData(token);
    } catch (err) {
      setError('Failed to delete AI app');
    }
  };

  const resetAppForm = () => {
    setShowAppForm(false);
    setEditingApp(null);
    setAppForm({
      name: '', description: '', category: 'kids', url: '', icon: '🤖',
      age_range: '', duration: '', is_coming_soon: false,
      bg_color: 'from-blue-50 to-indigo-50', border_color: 'border-blue-300'
    });
  };

  // Contact delete function
  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData(token);
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  // User CRUD functions
  const handleSaveUser = async () => {
    const token = getToken();
    try {
      if (editingUser) {
        const updateData = { name: userForm.name, email: userForm.email, phone: userForm.phone, role: userForm.role };
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/users`, userForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      fetchAllData(token);
      resetUserForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name, email: user.email, phone: user.phone, role: user.role, password: ''
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/admin/users/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData(token);
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    const token = getToken();
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-status`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData(token);
    } catch (err) {
      setError('Failed to toggle user status');
    }
  };

  const handleResetPassword = async (userId) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setResetPasswordResult(response.data);
      setShowResetModal(true);
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const resetUserForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
    setUserForm({ name: '', email: '', phone: '', role: 'Client', password: '' });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-home-48/artifacts/km5sisnl_PSN%20LOGO%20-%20Canvas%20%234.png" 
                alt="PSN Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
              <p className="text-gray-600">Enter your admin password</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <form onSubmit={handleLogin}>
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-2">Admin Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="admin-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  data-testid="admin-login-btn"
                  className="w-full bg-blue-800 text-white font-semibold py-3 rounded-lg hover:bg-blue-900 transition-colors"
                >
                  Sign In
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('contacts')}
              data-testid="tab-contacts"
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'contacts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare className="inline mr-2" size={18} />
              Contacts ({submissions.length})
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              data-testid="tab-articles"
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'articles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="inline mr-2" size={18} />
              Articles ({articles.length})
            </button>
            <button
              onClick={() => setActiveTab('ai-apps')}
              data-testid="tab-ai-apps"
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'ai-apps'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Sparkles className="inline mr-2" size={18} />
              AI Apps ({aiApps.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              data-testid="tab-users"
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="inline mr-2" size={18} />
              User Management ({users.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
            <button onClick={() => setError('')} className="float-right">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
          </div>
        ) : (
          <>
            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-4" data-testid="contacts-tab-content">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Submissions</h2>
                {submissions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No contact submissions yet.</p>
                ) : (
                  submissions.map((submission) => (
                    <div key={submission.id} className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {submission.first_name} {submission.last_name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            {submission.service_type || 'Not specified'}
                          </span>
                          <button
                            onClick={() => handleDeleteContact(submission.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                            title="Delete Contact"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Mail size={16} className="mr-2" />
                          <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                            {submission.email}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <Phone size={16} className="mr-2" />
                          <a href={`tel:${submission.phone}`} className="text-blue-600 hover:underline">
                            {submission.phone}
                          </a>
                        </div>
                        <div className="flex items-center md:col-span-2">
                          <Calendar size={16} className="mr-2" />
                          {new Date(submission.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-700">{submission.message}</p>
                      </div>
                      {submission.has_attachments && (
                        <div className="mt-2 text-sm text-gray-500">
                          📎 Attachments: {submission.attachment_names?.join(', ')}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Articles Tab */}
            {activeTab === 'articles' && (
              <div className="space-y-4" data-testid="articles-tab-content">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-800">Manage Articles</h2>
                  <button
                    onClick={() => setShowArticleForm(true)}
                    className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Article
                  </button>
                </div>

                {/* Article Form Modal */}
                {showArticleForm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-bold">{editingArticle ? 'Edit Article' : 'Add New Article'}</h3>
                          <button onClick={resetArticleForm} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={articleForm.title}
                              onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                              value={articleForm.category}
                              onChange={(e) => {
                                const cat = categoryOptions.find(c => c.value === e.target.value);
                                setArticleForm({
                                  ...articleForm,
                                  category: e.target.value,
                                  category_color: cat?.color || 'bg-blue-100 text-blue-800',
                                  border_color: cat?.border || 'border-t-blue-500'
                                });
                              }}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              {categoryOptions.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.value}</option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                              <input
                                type="text"
                                value={articleForm.author}
                                onChange={(e) => setArticleForm({...articleForm, author: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Read Time</label>
                              <input
                                type="text"
                                value={articleForm.read_time}
                                onChange={(e) => setArticleForm({...articleForm, read_time: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={articleForm.description}
                              onChange={(e) => setArticleForm({...articleForm, description: e.target.value})}
                              rows={2}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                            <textarea
                              value={articleForm.content}
                              onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                              rows={8}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex justify-end space-x-3">
                            <button onClick={resetArticleForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveArticle}
                              className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                            >
                              <Save size={18} className="mr-2" />
                              Save Article
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {articles.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No articles yet. Click "Add Article" to create one.</p>
                ) : (
                  articles.map((article) => (
                    <div key={article.id} className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${article.border_color}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${article.category_color}`}>
                              {article.category}
                            </span>
                            <span className="text-sm text-gray-500">{article.read_time}</span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800">{article.title}</h3>
                          <p className="text-gray-600 text-sm mt-1">{article.description}</p>
                          <p className="text-gray-500 text-xs mt-2">By {article.author}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEditArticle(article)}
                            className="flex items-center px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.id)}
                            className="flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* AI Apps Tab */}
            {activeTab === 'ai-apps' && (
              <div className="space-y-4" data-testid="ai-apps-tab-content">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-800">Manage AI Apps</h2>
                  <button
                    onClick={() => setShowAppForm(true)}
                    className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                  >
                    <Plus size={18} className="mr-2" />
                    Add AI App
                  </button>
                </div>

                {/* AI App Form Modal */}
                {showAppForm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-bold">{editingApp ? 'Edit AI App' : 'Add New AI App'}</h3>
                          <button onClick={resetAppForm} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">App Name</label>
                            <input
                              type="text"
                              value={appForm.name}
                              onChange={(e) => setAppForm({...appForm, name: e.target.value})}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                              <select
                                value={appForm.category}
                                onChange={(e) => setAppForm({...appForm, category: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="kids">For Kids</option>
                                <option value="parents">For Parents & Clients</option>
                                <option value="students">For Students & Learners</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
                              <input
                                type="text"
                                value={appForm.icon}
                                onChange={(e) => setAppForm({...appForm, icon: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={appForm.description}
                              onChange={(e) => setAppForm({...appForm, description: e.target.value})}
                              rows={3}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">App URL</label>
                            <input
                              type="url"
                              value={appForm.url}
                              onChange={(e) => setAppForm({...appForm, url: e.target.value})}
                              placeholder="https://example.com"
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Age Range (optional)</label>
                              <input
                                type="text"
                                value={appForm.age_range}
                                onChange={(e) => setAppForm({...appForm, age_range: e.target.value})}
                                placeholder="Ages 4-9"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (optional)</label>
                              <input
                                type="text"
                                value={appForm.duration}
                                onChange={(e) => setAppForm({...appForm, duration: e.target.value})}
                                placeholder="5-10 minutes"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="comingSoon"
                              checked={appForm.is_coming_soon}
                              onChange={(e) => setAppForm({...appForm, is_coming_soon: e.target.checked})}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <label htmlFor="comingSoon" className="ml-2 text-sm text-gray-700">
                              Mark as "Coming Soon"
                            </label>
                          </div>
                          <div className="flex justify-end space-x-3">
                            <button onClick={resetAppForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveApp}
                              className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                            >
                              <Save size={18} className="mr-2" />
                              Save AI App
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {aiApps.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No AI apps yet. Click "Add AI App" to create one.</p>
                ) : (
                  aiApps.map((app) => (
                    <div key={app.id} className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${app.border_color}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4">
                          <span className="text-4xl">{app.icon}</span>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-800">{app.name}</h3>
                              {app.is_coming_soon && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Coming Soon</span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm">{app.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span className="capitalize">{app.category === 'kids' ? 'For Kids' : app.category === 'parents' ? 'For Parents' : 'For Students'}</span>
                              {app.age_range && <span>{app.age_range}</span>}
                              {app.duration && <span>{app.duration}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEditApp(app)}
                            className="flex items-center px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app.id)}
                            className="flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4" data-testid="users-tab-content">
                {/* Sub-tabs */}
                <div className="flex space-x-4 mb-6">
                  <button
                    onClick={() => setUserSubTab('users')}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                      userSubTab === 'users' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Users size={18} className="mr-2" />
                    Users ({users.length})
                  </button>
                  <button
                    onClick={() => setUserSubTab('history')}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                      userSubTab === 'history' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <History size={18} className="mr-2" />
                    Login History
                  </button>
                  <button
                    onClick={() => setUserSubTab('visitors')}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                      userSubTab === 'visitors' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <BarChart3 size={18} className="mr-2" />
                    Visitor Analytics
                  </button>
                </div>

                {/* Users Sub-tab */}
                {userSubTab === 'users' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-800">Registered Users</h2>
                      <button
                        onClick={() => setShowUserForm(true)}
                        data-testid="add-user-btn"
                        className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                      >
                        <Plus size={18} className="mr-2" />
                        Add User
                      </button>
                    </div>

                    {/* User Form Modal */}
                    {showUserForm && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                          <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-xl font-bold">{editingUser ? 'Edit User' : 'Add New User'}</h3>
                              <button onClick={resetUserForm} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                              </button>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <input
                                  type="text"
                                  value={userForm.name}
                                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                  type="email"
                                  value={userForm.email}
                                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                  type="tel"
                                  value={userForm.phone}
                                  onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                  value={userForm.role}
                                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="Client">Client</option>
                                  <option value="Staff">Staff</option>
                                </select>
                              </div>
                              {!editingUser && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                  <input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Minimum 8 characters"
                                    required={!editingUser}
                                    minLength={8}
                                  />
                                </div>
                              )}
                              <div className="flex justify-end space-x-3 pt-2">
                                <button onClick={resetUserForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveUser}
                                  className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                                >
                                  <Save size={18} className="mr-2" />
                                  {editingUser ? 'Update' : 'Create'} User
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reset Password Modal */}
                    {showResetModal && resetPasswordResult && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Key size={32} className="text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Password Reset Successful</h3>
                            <p className="text-gray-600 mb-4">
                              New password for <strong>{resetPasswordResult.user_email}</strong>:
                            </p>
                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                              <code className="text-lg font-mono text-blue-800">{resetPasswordResult.temporary_password}</code>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">
                              Please share this password with the user securely. They should change it after logging in.
                            </p>
                            <button
                              onClick={() => { setShowResetModal(false); setResetPasswordResult(null); }}
                              className="px-6 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {users.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No users registered yet.</p>
                    ) : (
                      <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                              <tr key={user.id} className={!user.is_active ? 'bg-gray-50' : ''}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                  <div className="text-sm text-gray-500">Joined {new Date(user.created_at).toLocaleDateString()}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{user.email}</div>
                                  <div className="text-sm text-gray-500">{user.phone}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    user.role === 'Staff' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {user.role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {user.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => handleEditUser(user)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Edit User"
                                    >
                                      <Edit size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleToggleUserStatus(user.id)}
                                      className={`p-2 rounded ${user.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                                      title={user.is_active ? 'Deactivate User' : 'Activate User'}
                                    >
                                      {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                                    </button>
                                    <button
                                      onClick={() => handleResetPassword(user.id)}
                                      className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                                      title="Reset Password"
                                    >
                                      <Key size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                                      title="Delete User"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {/* Login History Sub-tab */}
                {userSubTab === 'history' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-800">Login History</h2>
                      <button
                        onClick={() => fetchAllData(getToken())}
                        className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        <RefreshCw size={18} className="mr-2" />
                        Refresh
                      </button>
                    </div>

                    {loginHistory.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No login history yet.</p>
                    ) : (
                      <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Time</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {loginHistory.map((record) => (
                              <tr key={record.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{record.user_name}</div>
                                  <div className="text-sm text-gray-500">{record.user_email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    record.user_role === 'Staff' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {record.user_role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(record.login_time).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {record.ip_address || 'Unknown'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {/* Visitor Analytics Sub-tab */}
                {userSubTab === 'visitors' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-800">Visitor Analytics</h2>
                      <button
                        onClick={() => fetchAllData(getToken())}
                        className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        <RefreshCw size={18} className="mr-2" />
                        Refresh
                      </button>
                    </div>

                    {!visitorStats ? (
                      <p className="text-gray-500 text-center py-8">No visitor data available yet.</p>
                    ) : (
                      <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center">
                              <div className="p-3 bg-blue-100 rounded-lg">
                                <BarChart3 size={24} className="text-blue-600" />
                              </div>
                              <div className="ml-4">
                                <p className="text-sm text-gray-500">Total Page Views</p>
                                <p className="text-2xl font-bold text-gray-800">{visitorStats.total_page_views}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center">
                              <div className="p-3 bg-green-100 rounded-lg">
                                <Users size={24} className="text-green-600" />
                              </div>
                              <div className="ml-4">
                                <p className="text-sm text-gray-500">Unique Sessions</p>
                                <p className="text-2xl font-bold text-gray-800">{visitorStats.unique_sessions}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center">
                              <div className="p-3 bg-purple-100 rounded-lg">
                                <FileText size={24} className="text-purple-600" />
                              </div>
                              <div className="ml-4">
                                <p className="text-sm text-gray-500">Pages Tracked</p>
                                <p className="text-2xl font-bold text-gray-800">{Object.keys(visitorStats.pages || {}).length}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Page Views by Page */}
                        {visitorStats.pages && Object.keys(visitorStats.pages).length > 0 && (
                          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Page Views by Page</h3>
                            <div className="space-y-3">
                              {Object.entries(visitorStats.pages).map(([page, count]) => (
                                <div key={page} className="flex items-center">
                                  <div className="flex-1">
                                    <div className="flex justify-between mb-1">
                                      <span className="text-sm text-gray-600">{page}</span>
                                      <span className="text-sm font-medium text-gray-800">{count} views</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{ width: `${(count / visitorStats.total_page_views) * 100}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent Page Views */}
                        {visitorStats.recent_views && visitorStats.recent_views.length > 0 && (
                          <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Page Views</h3>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {visitorStats.recent_views.slice(0, 20).map((view, idx) => (
                                    <tr key={idx}>
                                      <td className="px-4 py-2 text-sm text-gray-800">{view.page}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">
                                        {new Date(view.timestamp).toLocaleString()}
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-500 font-mono">
                                        {view.session_id?.substring(0, 8) || 'N/A'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Admin;
